import { useCallback } from 'react';
import { TextAndImageSelectionField } from 'components';
import FaceToFace from '@app/illu/Large/FaceToFace.svg';
import Unmoderated from '@app/illu/Large/Unmoderated.svg';
import Discuss from '@app/illu/Large/Discuss.svg';

import { createUseStyles, useTheme } from 'react-jss';
import { QuoteInformation, StudySubject, StudyType } from '@app/api';
import { Theme } from '@app/theme';
import { useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';

const useStyles = createUseStyles((theme: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'center',
    gap: theme.marginBase * 2,

    [theme.media.tablet]: {
      flexDirection: 'column',
    },
  },
}));

interface Props {
  hideDiary?: boolean;
}

export const StudyTypeSection = ({ hideDiary }: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();
  const { values, setFieldValue, resetForm } =
    useFormikContext<Partial<QuoteInformation>>();

  const { source, studySubject } = values;

  const onStudyTypeChange = useCallback(
    (studyType: string) => {
      resetForm();
      setFieldValue('source', source);
      setFieldValue('studySubject', studySubject);
      setFieldValue('studyType', studyType);
    },
    [source, studySubject, resetForm, setFieldValue],
  );

  return (
    <div className={classes.container}>
      <TextAndImageSelectionField
        illustration={FaceToFace}
        title={t('studySetup.generalSetup.studyType.moderated.title')}
        description={t(
          'studySetup.generalSetup.studyType.moderated.description',
        )}
        name="studyType"
        value={StudyType.MODERATED}
        onChange={onStudyTypeChange}
        trackingName="Study Type"
      />
      <TextAndImageSelectionField
        illustration={Unmoderated}
        title={t('studySetup.generalSetup.studyType.unmoderated.title')}
        description={t(
          'studySetup.generalSetup.studyType.unmoderated.description',
        )}
        name="studyType"
        value={StudyType.UNMODERATED}
        onChange={onStudyTypeChange}
        trackingName="Study Type"
      />
      {source !== 'CLIENT' && !hideDiary && (
        <TextAndImageSelectionField
          illustration={Discuss}
          title={t('studySetup.generalSetup.studyType.diary.title')}
          description={t('studySetup.generalSetup.studyType.diary.description')}
          name="studyType"
          value={StudyType.DIARY}
          onChange={onStudyTypeChange}
          topChip={{
            label: t('studySetup.generalSetup.studyType.diary.new'),
            color: 'accent',
            position: 'center',
          }}
          trackingName="Study Type"
        />
      )}
    </div>
  );
};
